import React from "react";
import cns from "classnames";
import { useObserver } from "mobx-react";
import { Box, Grid, makeStyles } from "@material-ui/core";
import locale from "@bonliva-crm/locale";
import { DrivingLicenceTypeInput, NorwayConsultantStatus } from "@bonliva-crm/graphql";
import { ThemeType } from "@theme";
import { Checkbox, Select, Switch, Text } from "@components/formx";
import { InputLookupAdornment } from "@components/InputLookupAdornment";
import { WithTooltip } from "@common";
import { useConsultantEditorContext } from "../ConsultantEditorContext";

export function PersonalInfo() {
  const editorStore = useConsultantEditorContext();

  const classes = useStyles({});

  return useObserver(() => {
    const seLookupAdornment = (
      <InputLookupAdornment
        disabled={!editorStore.canLookupSeSSN}
        loading={editorStore.bisnodeLookup.isLoading}
        onLookup={() => editorStore.ssnLookup("Sweden")}
      />
    );

    const noLookupAdornment = (
      <InputLookupAdornment
        disabled={!editorStore.canLookupNoSSN}
        loading={editorStore.bisnodeLookup.isLoading}
        onLookup={() => editorStore.ssnLookup("Norway")}
      />
    );

    return (
      <Grid container spacing={1}>
        <Grid item xs={12} sm={6}>
          <Text
            name="personal.firstName"
            label={locale.FirstName}
            TextFieldProps={{
              required: true,
              fullWidth: true,
              className: cns({
                [classes.notApproved]:
                  editorStore.consultantCVManager.keyNotApproved("personal.firstName")
              })
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Text
            name="personal.lastName"
            label={locale.LastName}
            TextFieldProps={{
              required: true,
              fullWidth: true,
              className: cns({
                [classes.notApproved]:
                  editorStore.consultantCVManager.keyNotApproved("personal.lastName")
              })
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Text
            name="personal.ssn"
            label={
              editorStore.isSsnDuplicated
                ? locale.SsnAlreadyAssigned
                : locale["Personal number"]
            }
            helperText={locale["Personal number format"]}
            TextFieldProps={{
              fullWidth: true,
              InputProps: {
                endAdornment: seLookupAdornment
              },
              error: editorStore.isSsnDuplicated || !editorStore.isValid("personal.ssn")
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Select
            name="personal.countries"
            label={locale["Can work in"]}
            fullWidth
            required
            multi
            options={editorStore.allCountries.toLabelValue(locale.getCountryName)}
          />
        </Grid>

        {editorStore.showNorwegianSSN && (
          <Grid item xs={12} sm={12}>
            <Text
              label={locale.NorwegianSsn}
              name="personal.norwegianSsn"
              TextFieldProps={{
                fullWidth: true,
                InputProps: {
                  startAdornment: (
                    <WithTooltip title={locale.NorwaySnnIsWorked} placement="top">
                      <span>
                        <Checkbox name={`personal.isWorkedNorway`} />
                      </span>
                    </WithTooltip>
                  ),
                  endAdornment: noLookupAdornment
                }
              }}
            />
          </Grid>
        )}

        {editorStore.showNorwegianSSN && editorStore.hasDoctorProfession && (
          <Grid item xs={12} sm={12}>
            <Text
              label={locale.CommentAuthorization}
              name="personal.commentAuthorization"
              TextFieldProps={{
                fullWidth: true,
                InputProps: {
                  startAdornment: (
                    <WithTooltip
                      title={locale.NorwayCommentAuthorization}
                      placement="top"
                    >
                      <span>
                        <Checkbox name={`personal.isApprovedAuthorization`} />
                      </span>
                    </WithTooltip>
                  )
                }
              }}
            />
          </Grid>
        )}

        {editorStore.showNorwegianSSN && (
          <Grid item xs={12} sm={12}>
            <Text
              label={locale.HPRNumber}
              name="personal.hprNumber"
              TextFieldProps={{
                fullWidth: true
              }}
            />
          </Grid>
        )}

        {editorStore.showNorwegianSSN && (
          <Grid item xs={12} sm={12}>
            <Select
              name="personal.norwayConsultantStatus"
              label={"Relationstatus"}
              fullWidth
              disabled={!editorStore.form.values.personal.hprNumber}
              options={Object.values(NorwayConsultantStatus).toLabelValue(
                (x) => locale.consultantStatusOptions[x]
              )}
            />
          </Grid>
        )}
        {editorStore.showInitialCommentField && (
          <Grid item xs={12}>
            <Text
              name="personal.firstComment"
              label={locale["First comment"]}
              TextFieldProps={{
                multiline: true,
                fullWidth: true
              }}
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <Text
            label={locale.IntroTitle}
            helperText={locale.IntroLabel}
            maxLength={800}
            allowMax={true}
            lengthCountFunc={(v) => (v.split("\n").length - 1) * 80 + v.length}
            name="personal.intro"
            TextFieldProps={{
              multiline: true,
              fullWidth: true
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <Text
            label={locale.SummaryAmbitionSection}
            helperText={locale.AboutYouLabel}
            name="personal.about"
            TextFieldProps={{
              multiline: true,
              fullWidth: true,
              className: cns({
                [classes.notApproved]:
                  editorStore.consultantCVManager.keyNotApproved("personal.about")
              })
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <Text
            label={locale.ImportantToYouTitle}
            helperText={locale.ImportantToYouLabel}
            name="personal.important"
            TextFieldProps={{
              multiline: true,
              fullWidth: true
            }}
          />
        </Grid>

        <Grid
          item
          xs={12}
          className={cns({
            [classes.notApproved]: editorStore.consultantCVManager.keyNotApproved(
              "personal.hasDrivingLicence"
            )
          })}
        >
          <Box display="flex" alignItems="end">
            <div>
              <Switch
                name={"personal.hasDrivingLicence"}
                label={locale.HasDrivingLicence}
              />
            </div>

            <div style={{ minWidth: "20rem" }}>
              <Select
                name="personal.drivingLicenceType"
                label="Körkort typ"
                disabled={!editorStore.form.values.personal.hasDrivingLicence}
                clearable={false}
                options={[
                  { label: "Ej inställt", value: DrivingLicenceTypeInput.NotSet },
                  { label: "Manuell", value: DrivingLicenceTypeInput.Manual },
                  { label: "Automat", value: DrivingLicenceTypeInput.Automatic }
                ]}
              />
            </div>
          </Box>
        </Grid>
      </Grid>
    );
  });
}

const useStyles = makeStyles((theme: ThemeType) => ({
  notApproved: {
    ...theme.palette.error
  }
}));
